import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerSiteAdministrationControllerService } from '@app/generated/api';
import { CustomerSiteDto } from '@app/generated/model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { buildCustomerSiteForm } from './customer-site-form-builder';

@Component({
  selector: 'app-edit-customer-site',
  templateUrl: './edit-customer-site.component.html',
  styleUrls: ['./edit-customer-site.component.scss'],
  standalone: false,
})
export class EditCustomerSiteComponent implements OnInit, OnDestroy {
  form!: UntypedFormGroup;
  loading = true;
  private siteCode: string | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private customerSiteService: CustomerSiteAdministrationControllerService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.siteCode = params.get('siteCode');

      if (!this.siteCode) {
        this.onClose();
        return;
      }

      this.loadCustomerSite();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onReset(): void {
    this.loadCustomerSite();
  }

  onSave(): void {
    if (!this.form.valid || !this.siteCode) {
      return;
    }

    const previousSiteCode = this.siteCode;
    const data = this.form.getRawValue() as CustomerSiteDto;
    const nextSiteCode = data.siteCode;

    this.customerSiteService
      .updateCustomerSite(previousSiteCode, data)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.form.markAsPristine();

          if (nextSiteCode && nextSiteCode !== previousSiteCode) {
            this.router.navigate(['/admin/customer-sites', nextSiteCode]);
            return;
          }

          this.siteCode = previousSiteCode;
        },
        error: (err) => console.error('Error updating customer site:', err),
      });
  }

  onDelete(): void {
    if (!this.siteCode) {
      return;
    }

    const shouldDelete = window.confirm(`Delete customer site mapping \"${this.siteCode}\"?`);
    if (!shouldDelete) {
      return;
    }

    this.customerSiteService
      .deleteCustomerSite(this.siteCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.onClose(),
        error: (err) => console.error('Error deleting customer site:', err),
      });
  }

  onClose(): void {
    this.router.navigateByUrl('/admin/customer-sites');
  }

  private loadCustomerSite(): void {
    const siteCode = this.siteCode;
    if (!siteCode) {
      this.loading = false;
      return;
    }

    this.loading = true;
    this.customerSiteService
      .getCustomerSite(siteCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (customerSite) => {
          this.form = buildCustomerSiteForm(this.formBuilder, customerSite);
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }
}
